import {
  FC,
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "react-toastify";
import { ZipProjectContext } from "@app/projects/zip-projects/ZipProjectContext";
import ProjectionProviderByEnum from "@app/projects/servicing-provider/projectionProviderByEnum";
import { ProviderActionsTabState } from "@app/projects/network-assessment/provider-actions/interfaces";
import { ProcessStatusEnum, ProviderActionsTabsEnum } from "@app/projects/network-assessment/provider-actions/enumerators";
import { DEFAULT_RECORDS_PER_PAGE } from "@app/projects/network-assessment/provider-actions/ProviderActionsDisplayFunctions";
import { FlyoutContext } from "@app/shared/Flyouts";
import { WafflesPaginationProps } from "@app/shared/waffles";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { getProviderChangeRoleGate } from "@app/projects/providerChangeRoleGate";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";

interface ProviderActionsDrawerContextProps {
  excludedTabState: ProviderActionsTabState;
  handleOverlayClick: () => void;
  isAnyTabLoading: boolean;
  isChildFlyoutOpen: boolean;
  isLoading: boolean;
  isProcessAllDisabled: boolean;
  isZipProject: boolean;
  /** Count of rule type tabs that have completed their initial data load, capped to the user's accessible tabs. */
  loadedTabCount: number;
  notifyTerminationsChanged?: (hasUnprocessed: boolean) => void;
  overlayClickToken: number;
  processAll: (shouldSkipTerminationTimeDistanceRecalc?: boolean) => Promise<void>;
  /** Human-readable explanation of why Process All is disabled due to missing roles, or null if not role-related. */
  processAllDisabledReason: string | null;
  projectId: number;
  requestRulesRefresh: (tabs?: ProviderActionsTabsEnum[]) => void;
  rulesRefreshTokens: Record<ProviderActionsTabsEnum, number>;
  selectedTab: ProviderActionsTabsEnum;
  setExcludedTabState: React.Dispatch<React.SetStateAction<ProviderActionsTabState>>;
  setIsChildFlyoutOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedTab: React.Dispatch<React.SetStateAction<ProviderActionsTabsEnum>>;
  setTargetedTabState: React.Dispatch<React.SetStateAction<ProviderActionsTabState>>;
  setTerminatedTabState: React.Dispatch<React.SetStateAction<ProviderActionsTabState>>;
  showNetworkIntegrityReason: boolean;
  targetedTabState: ProviderActionsTabState;
  terminatedTabState: ProviderActionsTabState;
  /** Role-aware total: 2 without termination access, 3 with TERM_PROVIDER_ADMIN or TERM_PROVIDER_VIEWER. */
  totalTabCount: number;
  unprocessedCounts?: {
    targeted: number;
    excluded: number;
    terminated: number;
    projectionTotal: number;
    total: number;
  };
}

const ProviderActionsDrawerContext = createContext<Partial<ProviderActionsDrawerContextProps>>({});

const createInitialPagination = (): Record<ProcessStatusEnum, WafflesPaginationProps> => {
   
  const basePagination: WafflesPaginationProps = {
    changePage: () => { },
    handlePageSizeChange: () => { },
    recordsPerPage: DEFAULT_RECORDS_PER_PAGE,
    resetToFirstPage: false,
    showPageSizeSelector: true,
    skip: 0,
    totalRecords: 0
 };
  return {
    [ProcessStatusEnum.Processed]: {
      ...basePagination
    },
    [ProcessStatusEnum.Unprocessed]: {
      ...basePagination
     
    },
  };
};

const createInitialTabState = (initialProcessStatus: ProcessStatusEnum): ProviderActionsTabState => {
  return {
    dataLoadFailed: false, 
    filteredProcessedRules: [],
    filteredUnprocessedRules: [],
    filters: {
      actionDateFrom: null,
      actionDateTo: null,
      pageIndex: 0,
      pageSize: DEFAULT_RECORDS_PER_PAGE,
      processStatus: initialProcessStatus,
      reason: [],
      recordingCriteria: [],
      searchByInput: "",
      searchByOption: ProjectionProviderByEnum.Name,
      specialtyGroupCodes: []
    },
    isInitialized: false,
    isLoading: false,
    pagination: createInitialPagination(),
    processedRules: [],
    selectedProviderKeys: {
      [ProcessStatusEnum.Processed]: [],
      [ProcessStatusEnum.Unprocessed]: []
    },
    unprocessedRules: []
  };
};

const ProviderActionsDrawerProvider: FC<{ children: ReactNode }> = (props: { children: ReactNode }) => {
  const zipProjectContext = useContext(ZipProjectContext);
  const countyProjectContext = useContext(CountyProjectContext);
  const isZipProject = zipProjectContext.dispatch !== null;
  const projectContext = isZipProject ? zipProjectContext.state : countyProjectContext;
  const featureFlagContext = useContext(FeatureFlagContext);
  const userContext = useContext(UserContext);
  const flyoutContext = useContext(FlyoutContext);
  const projectId = useMemo(() => projectContext.project.projectId, [projectContext.project.projectId]);
  const showNetworkIntegrityReason = (featureFlagContext.enabledFeatureFlags?.has(Features.ENABLE_NETWORK_INTEGRITY_IN_PROVIDER_HUB) ?? false) &&
    (projectContext.project?.showNetworkIntegrityDashboard ?? false);
  const [isChildFlyoutOpen, setIsChildFlyoutOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [overlayClickToken, setOverlayClickToken] = useState(0);

  const handleOverlayClick = useCallback(() => {
    setOverlayClickToken((t) => t + 1);
  }, []);
  const [selectedTab, setSelectedTab] = useState<ProviderActionsTabsEnum>(ProviderActionsTabsEnum.Targeted);

  const { flyoutRef } = flyoutContext;
  
  useEffect(() => {
    if (flyoutRef?.current) {
      flyoutRef.current.style.pointerEvents = isChildFlyoutOpen ? 'none' : '';
      flyoutRef.current.classList.toggle('provider-actions-drawer--child-flyout-open', isChildFlyoutOpen);
    }
  }, [isChildFlyoutOpen]);

  const initialTerminateStatus =
    userContext.roles.includes(Roles.TERM_PROVIDER_VIEWER) && !userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN)
      ? ProcessStatusEnum.Processed
      : ProcessStatusEnum.Unprocessed;

  const [targetedTabState, setTargetedTabState] = useState<ProviderActionsTabState>(() => createInitialTabState(ProcessStatusEnum.Unprocessed));
  const [excludedTabState, setExcludedTabState] = useState<ProviderActionsTabState>(() => createInitialTabState(ProcessStatusEnum.Unprocessed));
  const [terminatedTabState, setTerminatedTabState] = useState<ProviderActionsTabState>(() => createInitialTabState(initialTerminateStatus));

  const [rulesRefreshTokens, setRulesRefreshTokens] = useState<Record<ProviderActionsTabsEnum, number>>({
    [ProviderActionsTabsEnum.Targeted]: 0,
    [ProviderActionsTabsEnum.Excluded]: 0,
    [ProviderActionsTabsEnum.Terminated]: 0,
  });

  const requestRulesRefresh = useCallback((tabs?: ProviderActionsTabsEnum[]) => {
    const tabsToRefresh = tabs ?? [ProviderActionsTabsEnum.Targeted, ProviderActionsTabsEnum.Excluded, ProviderActionsTabsEnum.Terminated];

    setRulesRefreshTokens((prev) => {
      const next = { ...prev };

      tabsToRefresh.forEach((t) => {
        next[t] = (next[t] ?? 0) + 1;
      });

      return next;
    });
  }, []);

  const unprocessedCounts = useMemo(() => {
    const targeted = targetedTabState.unprocessedRules.length;
    const excluded = excludedTabState.unprocessedRules.length;
    const terminated = terminatedTabState.unprocessedRules.length;

    return {
      excluded,
      projectionTotal: targeted + excluded,
      targeted,
      terminated,
      total: targeted + excluded + terminated
    };
  }, [
    excludedTabState.unprocessedRules.length,
    targetedTabState.unprocessedRules.length,
    terminatedTabState.unprocessedRules.length
  ]);

  const isAnyTabLoading = useMemo(
    () => targetedTabState.isLoading || excludedTabState.isLoading || terminatedTabState.isLoading,
    [targetedTabState.isLoading, excludedTabState.isLoading, terminatedTabState.isLoading]
  );

  const hasTerminationAccess = useMemo(
    () => userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN) || userContext.roles.includes(Roles.TERM_PROVIDER_VIEWER),
    [userContext.roles],
  );

  const totalTabCount = hasTerminationAccess ? 3 : 2;

  const loadedTabCount = useMemo(() => {
    let count = 0;
    if (targetedTabState.isInitialized) count++;
    if (excludedTabState.isInitialized) count++;
    if (hasTerminationAccess && terminatedTabState.isInitialized) count++;
    return count;
  }, [excludedTabState.isInitialized, hasTerminationAccess, targetedTabState.isInitialized, terminatedTabState.isInitialized]);

  const isRoleAuthorizationEnforced = featureFlagContext.enabledFeatureFlags?.has(Features.ENFORCE_PROVIDER_CHANGE_ROLE_AUTHORIZATION) ?? false;

  const missingRoleReasons = useMemo(
    () => (isRoleAuthorizationEnforced
      ? getProviderChangeRoleGate(userContext.roles, {
        hasExcludes: unprocessedCounts.excluded > 0,
        hasTargets: unprocessedCounts.targeted > 0,
        hasTerminations: unprocessedCounts.terminated > 0,
      }).missingRoleReasons
      : []),
    [isRoleAuthorizationEnforced, unprocessedCounts.targeted, unprocessedCounts.excluded, unprocessedCounts.terminated, userContext.roles]
  );

  const isProcessAllDisabled = useMemo(() => {
    if (!isRoleAuthorizationEnforced) {
      const hasProjectionProcessorRole = userContext.roles.includes(Roles.PROJECTION_PROCESSOR);
      const hasTerminationAdminRole = userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN);
      const canProcessProjections = hasProjectionProcessorRole && unprocessedCounts.projectionTotal > 0;
      const canProcessTerminations = hasTerminationAdminRole && unprocessedCounts.terminated > 0;

      return !(canProcessProjections || canProcessTerminations) || isAnyTabLoading || isLoading;
    }

    const hasAnyUnprocessed = unprocessedCounts.targeted > 0 || unprocessedCounts.excluded > 0 || unprocessedCounts.terminated > 0;

    return !hasAnyUnprocessed || missingRoleReasons.length > 0 || isAnyTabLoading || isLoading;
  }, [isAnyTabLoading, isLoading, isRoleAuthorizationEnforced, missingRoleReasons, unprocessedCounts.targeted, unprocessedCounts.excluded, unprocessedCounts.terminated, unprocessedCounts.projectionTotal, userContext.roles]);

  const processAllDisabledReason = useMemo(
    () => (isRoleAuthorizationEnforced && missingRoleReasons.length > 0 ? missingRoleReasons.join(" ") : null),
    [isRoleAuthorizationEnforced, missingRoleReasons]
  );

  const processAll = async (shouldSkipTerminationTimeDistanceRecalc?: boolean) => {
    if (isProcessAllDisabled) {
      return;
    }

    setIsLoading(true);

    try {
      if (isZipProject) {
        await zipProjectContext.onProcessProviderChanges(false);
      } else {
        await countyProjectContext.processProviderChanges(false, shouldSkipTerminationTimeDistanceRecalc);
      }

      toast.success("Successfully Processed Provider Action Rules", {
        autoClose: 4000,
        containerId: "z-index-9999",
        position: "top-center"
      });
    } catch {
      toast.error("There was an error Processing Provider Action Rules.", {
        autoClose: false,
        closeOnClick: false,
        containerId: "z-index-9999",
        position: "top-center"
      });
    } finally {
      setIsLoading(false);
      await flyoutContext.handleCloseFlyout();
    }
  };

  const notifyTerminationsChanged = useCallback((hasUnprocessed: boolean) => {
    if (!isZipProject) {
      countyProjectContext.setProjectHasUnprocessedTerminations(hasUnprocessed);
    }
  }, [countyProjectContext, isZipProject]);

  const contextValue = {
    excludedTabState,
    handleOverlayClick,
    isAnyTabLoading,
    isChildFlyoutOpen,
    isLoading,
    isProcessAllDisabled,
    isZipProject,
    loadedTabCount,
    notifyTerminationsChanged,
    overlayClickToken,
    processAll,
    processAllDisabledReason,
    projectId,
    requestRulesRefresh,
    rulesRefreshTokens,
    selectedTab,
    setExcludedTabState,
    setIsChildFlyoutOpen,
    setIsLoading,
    setSelectedTab,
    setTargetedTabState,
    setTerminatedTabState,
    showNetworkIntegrityReason,
    targetedTabState,
    terminatedTabState,
    totalTabCount,
    unprocessedCounts
  };

  return (
    <ProviderActionsDrawerContext.Provider value={contextValue}>
      {props.children}
    </ProviderActionsDrawerContext.Provider>
  );
};

export { ProviderActionsDrawerContext, ProviderActionsDrawerProvider };